import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import './MobileHomeDashboard.scss';
import { MetricsPlot } from '../../../Dashboard/DParser/types';
import KSChart from 'presentation/components/KSChart/KSChart';
import MobileStatItem from './MobileStatItem';
import { useQueueContext } from 'presentation/components/Queue/QueueContext';
import { filterAndSplitByPriority } from 'presentation/components/Queue/utils/componentFilteringUtils';
import { useAuthContext } from 'context/useAuthContext';

type Props = {
  chartData: MetricsPlot;
  readReport: boolean;
  trace: boolean;
};

function MobileHomeDashboard({ chartData, readReport, trace }: Props): JSX.Element {
  const navigate = useNavigate();
  const { locationId } = useParams();
  const { userId } = useAuthContext();
  const { state } = useQueueContext();
  const { requests } = state;
  const requestsOpen = useMemo(() => {
    if (!requests) return 0;
    return requests.filter(request => request.status === 'Open').length;
  }, [requests]);

  // Only count blockers in 'Open' (To-Do) status
  const todoBlockers = useMemo(() => {
    if (!requests) return 0;
    // Use the utility function to filter by Open status and get blocker requests
    return filterAndSplitByPriority(requests, 'Open').blockerRequests.length;
  }, [requests]);

  const mentionsRequests = useMemo(() => {
    if (!requests) return 0;
    return requests.filter(
      request =>
        request.status === 'Open' && request.comments?.some(comment => comment.taggedUserIds?.includes(userId!))
    ).length;
  }, [requests]);

  const handleStatClick = (filterType: 'Blocker' | 'Open' | 'In Progress' | 'Mentions') => {
    // Always navigate to new-dashboard now
    const basePath = `/app/location/${locationId}/new-dashboard`;
    // Start with the view=queue parameter
    const queryParams = new URLSearchParams('view=queue');

    // Add the specific filter parameter
    if (filterType === 'Blocker') {
      queryParams.set('priority', 'Blocker');
    } else if (filterType === 'Open') {
      queryParams.set('status', 'Open');
    } else if (filterType === 'In Progress') {
      queryParams.set('status', 'In Progress');
    } else if (filterType === 'Mentions') {
      queryParams.set('filterMentions', 'true');
    }

    // Navigate with the combined path and parameters
    navigate(`${basePath}?${queryParams.toString()}`);
  };

  return (
    <div className="MobileHomeDashboard">
      <div className="MobileHomeDashboard-header">
        <div className="MobileHomeDashboard-title">queue</div>
        <div className="MobileHomeDashboard-stats">
          {todoBlockers > 0 && (
            <MobileStatItem
              value={todoBlockers}
              label="BLOCKERS"
              blocker={true}
              onClick={() => handleStatClick('Blocker')}
            />
          )}
          {requestsOpen > 0 && (
            <MobileStatItem value={requestsOpen} label="TO-DO" onClick={() => handleStatClick('Open')} />
          )}
          {mentionsRequests > 0 && (
            <MobileStatItem
              value={mentionsRequests}
              label={mentionsRequests > 1 ? '@ MENTIONS' : '@MENTION'}
              onClick={() => handleStatClick('Mentions')}
            />
          )}
        </div>
      </div>
      <div className="MobileHomeDashboard-chartSection">
        <KSChart data={chartData} customHeight={320} />
      </div>
    </div>
  );
}

export default MobileHomeDashboard;
